/**
 * Zone bookkeeping: the only code that moves a card between zones.
 *
 * Every move goes through `moveInstance`, which keeps the ordered zone arrays, the
 * per-instance card states and — importantly for ticket 05 — `knownTo` consistent. Zone
 * conservation (every instance in exactly one zone array, exactly once) is an invariant the
 * fuzz suite checks (SPEC.md §9).
 */
import { definitionOf, effectiveStrengthOf } from './characteristics.ts';
import type { GameEvent } from './events.ts';
import type { InstanceId, PlayerIndex, Zone, ZonePlacement } from './ids.ts';
import { anyDeckTopReveal, deckTopIsRevealed, dropModifiersFor } from './modifiers.ts';
import type { CardInstance, GameState } from './state.ts';
import { allPlayersMask, instanceAt, playerAt, playerBit, zoneOf } from './state.ts';
import { shuffleInPlace } from './rng.ts';
import { cardsUnder, dissolveStack, leaveUnderPile, underCount } from './stacks.ts';

/** Record something that happened during the current `apply`. */
export function emit(state: GameState, event: GameEvent): void {
  state.events.push(event);
}

/**
 * How a card entering a discard registers the abilities that **function from there**
 * (CR §6.1.12) — "At the start of your turn, if this card is in your discard, …".
 *
 * Set once, at module load, by `abilities/interpret.ts`, which owns the ability registrations;
 * this module has no business importing it (that dependency would be circular), the same
 * deferred wiring `modifiers.ts` uses for `setTriggerRefresh`.
 */
export type DiscardRegistration = (state: GameState, instance: CardInstance) => void;

let registerDiscardAbilities: DiscardRegistration = () => {};

export function setDiscardRegistration(register: DiscardRegistration): void {
  registerDiscardAbilities = register;
}

/** Where in the target zone the card goes. Decks are ordered; other zones append. */
export type { ZonePlacement } from './ids.ts';

/**
 * Move a card to another zone, updating card states and `knownTo`.
 *
 * Leaving play clears the card's in-play states — exertion, damage counters (which "cease
 * to exist", CR §1.9) and drying, and emits `card-left-play` — the one place "when this
 * character leaves play" can be observed for every way of leaving at once.
 */
export function moveInstance(
  state: GameState,
  id: InstanceId,
  toZone: Zone,
  placement: ZonePlacement = 'top',
): void {
  const instance = instanceAt(state, id);
  const fromZone = instance.zone;
  const leftPlay = fromZone === 'play' && toZone !== 'play';
  // "1 or more cards have left a player's discard this turn" (The Horned King - Triumphant
  // Ghoul) counts every way a card leaves a discard — returned to hand, played from discard,
  // put under a card, shuffled into a deck, and so on — so it is recorded generically, right
  // here at the one place every move goes through, rather than at each individual verb.
  if (fromZone === 'discard' && toZone !== 'discard') {
    state.leftDiscardThisTurn.push(id);
    emit(state, {
      type: 'card-left-discard',
      player: instance.controller,
      instance: id,
      defId: instance.defId,
      to: toZone,
    });
  }
  // "Cards were put into your discard this turn" counts every cause and is keyed to the
  // destination discard's player. Every zone move converges here, including discards from
  // hand, Actions finishing, banishes, and cards scattered from a dissolved stack.
  if (fromZone !== 'discard' && toZone === 'discard') {
    playerAt(state, instance.owner).cardsPutIntoDiscardThisTurn += 1;
  }
  const leavingController = instance.controller;
  const from = playerAt(state, instance.controller);
  const fromList = zoneOf(from, instance.zone);
  const at = fromList.indexOf(id);
  if (at === -1) {
    throw new Error(`Instance ${id} is not in ${from.index}'s ${instance.zone} zone`);
  }
  fromList.splice(at, 1);
  // A card that leaves the deck is no longer one its owner is looking at (CR §7.9).
  const looking = from.looking.indexOf(id);
  if (looking !== -1) from.looking.splice(looking, 1);

  if (instance.zone === 'play') {
    instance.exerted = false;
    instance.damage = 0;
    instance.drying = false;
    // Being *at* a location is an in-play relationship on both sides (CR §4.3.6): a character
    // that leaves play is no longer anywhere, and a location that leaves play empties.
    instance.location = null;
    for (const other of state.instances) {
      if (other.location === id) other.location = null;
    }
    // Continuous effects applied to a card end when it leaves play, along with its damage
    // counters (CR §1.9) — a card that comes back is a new object as far as they go.
    dropModifiersFor(state, id);
    // A pile moves as one unit: cards under a card that leaves play go to their owners'
    // discard (CR §8.10.4). Banishing a stack, bouncing its top card and discarding it all
    // land here, which is why none of them knows about stacks (`stacks.ts`).
    dissolveStack(state, id);
  }
  // A card leaving the `under` zone by any route other than joining another pile detaches from
  // whichever pile held it (ticket 103) — `rules.ts`'s play-from-under path (ticket 63) already
  // called this itself before moving the card; every other relocation out of `under` (a bulk
  // "put all cards from under this character into your hand", the general `move-card`/
  // `put-into-hand`/`put-on-deck`/`put-into-inkwell` verbs) goes through here instead, so it is
  // handled once, generically, exactly like `dissolveStack` above. A no-op for a card that was
  // not the top of anything's pile bookkeeping already forgot on its own.
  if (instance.zone === 'under' && toZone !== 'under') {
    leaveUnderPile(state, id);
  }
  // Cards always return to their owner (CR §7.1.3).
  instance.controller = instance.owner;
  instance.zone = toZone;
  updateKnownTo(state, instance);

  const to = playerAt(state, instance.controller);
  const toList = zoneOf(to, toZone);
  if (toZone === 'deck' && placement === 'bottom') toList.push(id);
  else if (toZone === 'deck') toList.unshift(id);
  else toList.push(id);
  // Only play and the inkwell hold an exerted card; a card that leaves either straightens out
  // (CR §7 — a card entering a new zone is a new object with none of its old states).
  if (toZone !== 'play' && toZone !== 'inkwell') instance.exerted = false;

  // A face-up deck top follows the deck (`reveal-deck-top`) card by card: drawing two exposes
  // the second card the moment the first leaves, and it is face up while it is there. The
  // engine's between-steps pass is too coarse to see it — one draw-2 primitive is a single
  // step — so a card *leaving* a deck turns the next one over on the spot. Cards going the
  // other way are not read here: several cards put on top arrive as one instruction, and only
  // the card left on top when it finishes is turned face up (the between-steps pass).
  if (fromZone === 'deck') maintainDeckTopReveals(state);

  // Entering play registers a card's triggered abilities; entering a discard registers the
  // ones that function from *there* (CR §6.1.12), which is the only way a card discarded
  // straight out of a hand ever gets them.
  if (toZone === 'discard') registerDiscardAbilities(state, instance);

  if (leftPlay) {
    emit(state, {
      type: 'card-left-play',
      player: leavingController,
      instance: id,
      defId: instance.defId,
      to: toZone,
    });
  }
}

/** Everything `move-card` may say beyond "which card, which zone". */
export interface MoveCardOptions {
  readonly to: Zone;
  readonly placement?: ZonePlacement;
  readonly exerted?: boolean;
  readonly facedown?: boolean;
  readonly shuffle?: boolean;
}

/**
 * The `move-card` verb (`abilities/ir.ts`): relocate one card, wherever it is.
 *
 * `moveInstance` is still the only thing that moves a card; this is the rules-facing wrapper
 * that adds the three things printed text asks for on top of the move — landing exerted,
 * landing **facedown** (knowledge is not granted, so a card put into an inkwell off the top of
 * a deck stays unknown to *both* players), and shuffling the deck it lands in.
 *
 * It also emits the named event a destination deserves, so the ordinary triggers see it:
 * a card going from play to a hand is a `card-returned`, and a card entering an inkwell is a
 * `card-inked` — without setting the once-per-turn ink limit, which is a turn action, not this.
 */
export function moveCardTo(state: GameState, id: InstanceId, options: MoveCardOptions): void {
  const instance = instanceAt(state, id);
  const fromZone = instance.zone;
  const player = instance.controller;
  const defId = instance.defId;
  const knownBefore = instance.knownTo;

  moveInstance(state, id, options.to, options.placement ?? 'top');
  // "Facedown" is the one place knowledge is not monotone, as it is for a Boost card
  // (`stacks.ts`): nobody may look at a card put into an inkwell from a hidden zone.
  if (options.facedown === true) instance.knownTo = knownBefore;
  if (options.exerted === true && (instance.zone === 'play' || instance.zone === 'inkwell')) {
    instance.exerted = true;
  }

  if (options.to === 'hand' && fromZone === 'play') {
    emit(state, { type: 'card-returned', player, instance: id, defId, from: fromZone });
  }
  if (options.to === 'inkwell') {
    announceInked(state, player, id, defId);
  }
  // Discarding from a hand is its own named fact (CR §7.3); a card leaving *play* for a discard
  // is a banish, which has its own verb and never comes through here.
  if (options.to === 'discard' && fromZone === 'hand') {
    playerAt(state, instance.owner).cardsDiscardedThisTurn += 1;
    emit(state, { type: 'card-discarded', player, instance: id, defId });
  }
  if (options.shuffle === true && options.to === 'deck') {
    shuffleDeck(state, instance.controller);
  }
}

/**
 * Who knows this card's identity now that it is in `zone` (CONTEXT.md, SPEC.md §4.4).
 *
 * **Knowledge is monotone: it is only ever gained.** Every zone change either adds knowers
 * or leaves the mask alone — which is precisely what makes the SPEC.md §4.4 leaks fall out
 * rather than needing special cases: a card that goes to play and comes back to a hand
 * stays known to everyone who saw it, and a card revealed and then shuffled into a deck
 * stays known to whoever saw it. What a shuffle destroys is a card's *position*, not its
 * identity, and `knownTo` does not model positions — `playerView` accordingly never names a
 * deck card, and `determinize` deals the identities known to be in a deck to random
 * positions within it.
 */
function updateKnownTo(state: GameState, instance: CardInstance): void {
  const everyone = allPlayersMask(state.players.length);
  switch (instance.zone) {
    case 'play':
    case 'discard':
      instance.knownTo = everyone;
      return;
    case 'hand':
    case 'inkwell':
      // Your own inkwell is a documented leak: you know what you inked (CR §7.5).
      instance.knownTo |= playerBit(instance.controller);
      return;
    case 'deck':
      // Whoever knew this card still knows it is in there; they just lose track of where.
      return;
    case 'under':
      // A card under another keeps whatever was known about it: a Shift base was in play, so
      // it stays public. A **facedown** under-card (Boost) is unviewable by anyone (CR
      // §7.4.2.1), which `stacks.ts` records by clearing the mask outright as it puts the
      // card there — the one place knowledge is not monotone, and its single source.
      return;
  }
}

/**
 * "Look at the top N cards of your deck" (CR §7.9) — and, publicly, "reveal" them.
 *
 * The cards do not move. What changes is that their owner (or, for a reveal, everyone) knows
 * what they are *and where they are*, which is the one case a view names a card in a deck.
 * The set is transient: `stopLooking` clears it, and the interpreter clears it as the ability
 * that opened it finishes, so no ability can leave a player looking.
 */
export function lookAtDeck(
  state: GameState,
  player: PlayerIndex,
  count: number,
  reveal = false,
): readonly InstanceId[] {
  const owner = playerAt(state, player);
  const seen = owner.deck.slice(0, Math.max(0, count));
  for (const id of seen) {
    if (owner.looking.includes(id)) continue;
    owner.looking.push(id);
    if (reveal) revealToAll(state, id);
    else revealTo(state, id, playerBit(player));
  }
  return seen;
}

/**
 * Open the looking window for one card that is already in a deck (CR §7.9).
 *
 * `lookAtDeck` is the positional form — "the top 3 cards of your deck". This is the same act
 * aimed at a card a selector has already found, which is what revealing needs: naming a card
 * that is still in a deck has to say *where* it is as well as *what* it is, or `isVisibleTo`
 * redacts it on position and the face never shows. A no-op anywhere but a deck, where the
 * position rule does not apply in the first place.
 */
export function lookAtCard(state: GameState, id: InstanceId): void {
  const instance = instanceAt(state, id);
  if (instance.zone !== 'deck') return;
  const owner = playerAt(state, instance.controller);
  if (!owner.looking.includes(id)) owner.looking.push(id);
}

/** Stop looking — at one player's cards, or (with no player) at everybody's. */
export function stopLooking(state: GameState, player?: PlayerIndex): void {
  for (const seat of state.players) {
    if (player === undefined || seat.index === player) seat.looking = [];
  }
}

/**
 * "…and put it into your hand." A card goes to its **owner**'s hand (CR §7.1.3).
 *
 * Deliberately emits nothing: this is not a draw (CR §7.2 — a "whenever you draw a card"
 * ability must not see it) and not a return from play, and the two events that exist mean
 * exactly those things.
 */
export function putIntoHand(state: GameState, id: InstanceId): void {
  moveInstance(state, id, 'hand');
}

/** "Put it on the top / on the bottom of your deck" (CR §7.2). */
export function putOnDeck(state: GameState, id: InstanceId, placement: ZonePlacement): void {
  moveInstance(state, id, 'deck', placement);
}

/**
 * "Put [card] into your inkwell facedown and exerted" — an *effect* putting a card there
 * (CR §7.5), which is not the once-per-turn ink turn action and so does not spend it.
 *
 * It emits `card-inked` all the same: a card that triggers on a card entering your inkwell
 * means this too.
 */
export function putIntoInkwell(
  state: GameState,
  id: InstanceId,
  into?: PlayerIndex,
  options: { readonly exerted?: boolean; readonly facedown?: boolean } = {},
): void {
  const defId = instanceAt(state, id).defId;
  const knownBefore = instanceAt(state, id).knownTo;
  moveInstance(state, id, 'inkwell');
  const instance = instanceAt(state, id);
  // "Facedown" is the one place knowledge is not monotone (`moveCardTo` says the same): a card
  // inked out of a hidden zone stays unknown to *both* players.
  if (options.facedown === true) instance.knownTo = knownBefore;
  // "Put **that card** into **your** inkwell" — the card may be an opponent's, and a zone move
  // always returns a card to its owner (CR §7.1.3), so taking it into another player's inkwell
  // is a second, explicit step.
  if (into !== undefined && into !== instance.controller) {
    const from = playerAt(state, instance.controller).inkwell;
    from.splice(from.indexOf(id), 1);
    instance.controller = into;
    playerAt(state, into).inkwell.push(id);
    instance.knownTo |= playerBit(into);
  }
  instance.exerted = options.exerted ?? true;
  announceInked(state, instance.controller, id, defId);
}

/**
 * The one place a card entering an inkwell is announced (CR §7.5) — the `ink` turn action and
 * both effect-driven puts come through here.
 *
 * It counts the put as well as emitting the event, because "if it's the second card you've put
 * into your inkwell this turn" counts **cards**, not ink actions: `Player.inksThisTurn` is the
 * once-per-turn limit and must keep counting only the turn action.
 */
export function announceInked(
  state: GameState,
  player: PlayerIndex,
  id: InstanceId,
  defId: number,
): void {
  playerAt(state, player).cardsInkedThisTurn += 1;
  emit(state, { type: 'card-inked', player, instance: id, defId });
}

/** Discard a card from a hand (CR §7.3). It goes to its owner's discard. */
export function discardCard(state: GameState, id: InstanceId): void {
  const instance = instanceAt(state, id);
  const player = instance.controller;
  const owner = instance.owner;
  const defId = instance.defId;
  moveInstance(state, id, 'discard');
  playerAt(state, owner).cardsDiscardedThisTurn += 1;
  emit(state, { type: 'card-discarded', player, instance: id, defId });
}

/** Reveal a card to some players (CR §7.9) — `players` is a `knownTo` bitmask. */
export function revealTo(state: GameState, id: InstanceId, players: number): void {
  instanceAt(state, id).knownTo |= players;
}

/** Reveal a card to everyone — what a public reveal does to `knownTo`. */
export function revealToAll(state: GameState, id: InstanceId): void {
  revealTo(state, id, allPlayersMask(state.players.length));
}

/**
 * Keep `knownTo` caught up with the live `reveal-deck-top` statics ("Each player plays with
 * the top card of their deck face up"): whichever card is on top of a covered deck right now
 * is revealed to everyone. Run wherever a deck's top can change — every `moveInstance` into or
 * out of a deck, every shuffle — so a card that is on top only *between* the halves of one
 * primitive is face up while it is there, and once more between the engine's automatic steps,
 * which is what catches a static that has only just come into play.
 *
 * Deliberately monotone and idempotent — it only ever adds knowers, exactly as `revealTo`
 * does, so a card seen face up stays known after the static ends (`knownDeckDefIds`), while
 * `playerView` stops *naming* it the moment no live static covers the deck (`view.ts`'s
 * `isRevealedDeckTop` is the read half of this pair).
 */
export function maintainDeckTopReveals(state: GameState): void {
  // Asked on every deck mutation and every automatic engine step, so the games with no such
  // card anywhere on the board — nearly all of them — stop at one memoized lookup per card in
  // play instead of walking every static twice.
  if (!anyDeckTopReveal(state)) return;
  for (const player of state.players) {
    const top = player.deck[0];
    if (top === undefined) continue;
    if (deckTopIsRevealed(state, player.index)) revealToAll(state, top);
  }
}

/** Shuffle a player's deck, consuming the game RNG. */
export function shuffleDeck(state: GameState, player: PlayerIndex): void {
  const owner = playerAt(state, player);
  shuffleInPlace(owner.deck, state.rng);
  // The shuffle is the other way a deck's top card changes without a card moving zones.
  maintainDeckTopReveals(state);
  emit(state, { type: 'deck-shuffled', player });
}

/**
 * Draw one card. Drawing from an empty deck does nothing and is *not* a loss — since CR
 * 2.0 a player only loses if their turn **ends** with an empty deck (§1.8.1.2).
 *
 * The single chokepoint for `Player.cardsDrawnThisTurn` (mirrors `announceInked`'s role for
 * `cardsInkedThisTurn`) — every way a card reaches a hand by drawing goes through here, and a
 * failed draw off an empty deck does not count.
 */
export function drawCard(state: GameState, player: PlayerIndex): InstanceId | null {
  const owner = playerAt(state, player);
  const id = owner.deck[0];
  if (id === undefined) {
    emit(state, { type: 'draw-failed', player });
    return null;
  }
  moveInstance(state, id, 'hand');
  owner.cardsDrawnThisTurn += 1;
  emit(state, { type: 'card-drawn', player, instance: id, defId: instanceAt(state, id).defId });
  return id;
}

export function drawCards(state: GameState, player: PlayerIndex, count: number): void {
  for (let i = 0; i < count; i += 1) drawCard(state, player);
}

/**
 * Banish a card: it goes to its owner's discard (CR §7.6).
 *
 * The event carries the banish's **provenance**, because several cards trigger on it and the
 * CR distinguishes the cases: `source` is what banished the card, and `challengeRole` says
 * which side of a live challenge it was on. The challenge half is read off the resolution
 * stack rather than passed in, so a banish at the game state check that follows the damage
 * step — which is where a challenge banish actually happens — is attributed correctly without
 * the check knowing anything about challenges.
 *
 * This is the "it happens" half only (ticket 137) — every other module reaches it through
 * `banishEvent` (`damage.ts`, re-exported above), which raises a `BanishPendingEvent` through
 * the replacement pipeline first and only calls this when nothing catches it. Calling this
 * directly (as a handful of engine tests still do, for setup unrelated to banish itself) skips
 * that pipeline entirely — never do it from rules-facing code.
 */
export function banish(state: GameState, id: InstanceId, source: InstanceId | null = null): void {
  const instance = instanceAt(state, id);
  const player = instance.controller;
  const defId = instance.defId;
  const challenge = challengeContext(state, id);
  const location = instance.location;
  // Snapshotted before the move: `moveInstance` dissolves this card's pile, if it has one
  // (`stacks.ts`'s `dissolveStack`), so `underCount` would read 0 for every trigger collected
  // off the events below without this. Same reasoning as `location`, just above.
  const hadCardsUnder = underCount(state, id) > 0;
  // The pile itself (ticket 104), not just whether one existed — `cardsUnder` reads the same
  // live stack `hadCardsUnder` does, so it needs the same before-the-move snapshot.
  const underCards = [...cardsUnder(state, id)];
  // {S} as it was the instant before leaving play (ticket 104's Wreck-It Ralph) — a dynamic
  // static like POWERED UP stops contributing the moment the card is out of play, so this has
  // to be read now or never.
  const effectiveStrength = effectiveStrengthOf(state, instance);
  // Whether an exerted character stood here (ticket 104's The Frozen Vine) — meaningless for a
  // banished character (nothing is ever "at" a character), always false for one. Read before
  // the move because `moveInstance` clears every occupant's `location` the same instant it
  // clears this card's own, just like `location` above.
  const hadExertedCharacterHere = charactersAt(state, id).some((occupant) => occupant.exerted);
  moveInstance(state, id, 'discard');
  // The per-turn fact "a character was banished this turn" (any cause) is written exactly
  // where `card-banished` is emitted, so the two can never disagree; the Ready step clears it.
  // Every banish passes through this one chokepoint — challenge, ability, lethal non-challenge
  // damage, cost payment — so nothing else needs to touch it.
  state.banishedThisTurn.push(id);
  emit(state, {
    type: 'card-banished',
    player,
    instance: id,
    defId,
    source: challenge === null ? source : challenge.other,
    challengeRole: challenge === null ? null : challenge.role,
    location,
    hadCardsUnder,
    underCards,
    effectiveStrength,
    hadExertedCharacterHere,
  });
  // A banish performed by an ability whose trigger arose from the challenge happens *during*
  // that challenge (CR §4.6.6, the cards 71/50 clarification). The live-frame case defers this
  // event until the check cascade has quieted (`engine.ts`'s `announceChallengeBanishes`); here
  // the banish is already final, so the narrower event rides directly beside `card-banished`.
  if (challenge !== null && !challenge.live) {
    emit(state, {
      type: 'banished-in-challenge',
      player,
      instance: id,
      defId,
      by: challenge.other,
      role: challenge.role,
      hadCardsUnder,
      underCards,
      effectiveStrength,
    });
  }
}

/**
 * `hadCardsUnder`, read back off the `card-banished` event already on the log rather than the
 * board — for `announceChallengeBanishes` (`engine.ts`), which emits the deferred
 * `banished-in-challenge` event *after* a live challenge frame has popped, by which point the
 * card has been in the discard (and its pile, if any, dissolved) for a game state check or two.
 */
function findCardBanishedEvent(
  state: GameState,
  id: InstanceId,
): Extract<GameEvent, { type: 'card-banished' }> | undefined {
  for (let i = state.events.length - 1; i >= 0; i -= 1) {
    const event = state.events[i];
    if (event?.type === 'card-banished' && event.instance === id) return event;
  }
  return undefined;
}

export function hadCardsUnderWhenBanished(state: GameState, id: InstanceId): boolean {
  return findCardBanishedEvent(state, id)?.hadCardsUnder ?? false;
}

/**
 * `underCards`, read back the same way as `hadCardsUnderWhenBanished` — for
 * `announceChallengeBanishes` (`engine.ts`), whose deferred `banished-in-challenge` needs the
 * same pre-dissolve pile the already-emitted `card-banished` recorded (ticket 104).
 */
export function underCardsWhenBanished(state: GameState, id: InstanceId): readonly InstanceId[] {
  return findCardBanishedEvent(state, id)?.underCards ?? [];
}

/** `effectiveStrength`, read back the same way — see `underCardsWhenBanished` (ticket 104). */
export function effectiveStrengthWhenBanished(state: GameState, id: InstanceId): number {
  return findCardBanishedEvent(state, id)?.effectiveStrength ?? 0;
}

/**
 * The challenge this card is a participant of, if any (CR §4.6) — its side, and the other card.
 *
 * Two places on the resolution stack know about a challenge, checked innermost first:
 * - a **live** `challenge` frame, during its damage step and the check that follows;
 * - a `resolve-ability` frame whose item's trigger arose from a challenge banish (`bag.ts`'s
 *   `eventChallenge`) — that ability resolves "during that challenge" even though the frame
 *   has popped, so its banishes of the participants are attributed the same way. `live`
 *   distinguishes the two only for who emits `banished-in-challenge`.
 */
/**
 * Whether `id` is currently a participant of a live challenge, and which side — read off the
 * resolution stack rather than passed in, so a card whose provenance depends on a challenge
 * still resolving is attributed correctly without its caller knowing anything about challenges.
 * Exported for `damage.ts`, which attributes `damage-dealt`'s own `challengeRole` the same way.
 */
export function challengeContext(
  state: GameState,
  id: InstanceId,
): {
  readonly role: 'challenger' | 'defender';
  readonly other: InstanceId;
  readonly live: boolean;
} | null {
  for (let at = state.stack.length - 1; at >= 0; at -= 1) {
    const frame = state.stack[at];
    if (frame === undefined) continue;
    if (frame.kind === 'challenge' && frame.target !== null) {
      if (frame.challenger === id) return { role: 'challenger', other: frame.target, live: true };
      if (frame.target === id) return { role: 'defender', other: frame.challenger, live: true };
    }
    if (frame.kind === 'resolve-ability' && frame.item.challenge !== undefined) {
      const between = frame.item.challenge;
      if (between.challenger === id) {
        return { role: 'challenger', other: between.defender, live: false };
      }
      if (between.defender === id) {
        return { role: 'defender', other: between.challenger, live: false };
      }
    }
  }
  return null;
}

/**
 * Move a character to a location (CR §4.3.6). Moving is not a zone change: both cards stay in
 * play and only the relationship changes, so this is the one writer of `CardInstance.location`
 * besides the cleanup in `moveInstance`.
 *
 * A character already at another location moves from it — that is still "moving to a
 * location", and the event names where it came from.
 */
export function moveToLocation(state: GameState, id: InstanceId, location: InstanceId): void {
  const character = instanceAt(state, id);
  const from = character.location;
  if (from === location) return;
  character.location = location;
  emit(state, {
    type: 'character-moved',
    player: character.controller,
    instance: id,
    defId: character.defId,
    location,
    from,
  });
}

/** Characters standing at this location, in play order (CR §5.6's "here"). */
export function charactersAt(state: GameState, location: InstanceId): CardInstance[] {
  return state.instances.filter(
    (instance) => instance.zone === 'play' && instance.location === location,
  );
}

/**
 * The damage verbs live in `damage.ts` (ticket 11), where each one is a **pending event** that
 * damage modifiers and then replacement effects get a look at before the counters land
 * (CR §1.9, §6.5). They are re-exported here because damage is zone bookkeeping's neighbour
 * and every caller already imports it from this module.
 */
export {
  banishEvent,
  dealDamage,
  dealSimultaneousDamage,
  discardPrevented,
  gainLoreEvent,
  inkEvent,
  loseLore,
  moveDamage,
  putDamage,
  removeDamage,
} from './damage.ts';

/**
 * Return a card to its owner's hand — from play, or from a discard (CR §6.1.12's "return this
 * card to your hand" reaches a card in either, the interpreter's `return-to-hand` case is what
 * lets a discard card meet it in the first place). `from` records which one it actually left,
 * so a trigger can tell "from your discard" (ticket 76) apart from "from play".
 */
export function returnToHand(state: GameState, id: InstanceId): void {
  const instance = instanceAt(state, id);
  const player = instance.controller;
  const defId = instance.defId;
  const from = instance.zone;
  moveInstance(state, id, 'hand');
  emit(state, { type: 'card-returned', player, instance: id, defId, from });
}

/**
 * Add (or subtract) lore. Lore never goes below zero (CR §6.6.3).
 *
 * The raw counter mutation. A *gain* goes through `gainLoreEvent` (`damage.ts`) instead, where
 * a "can't gain lore" restriction and the replacement pipeline get their look first.
 *
 * `source` is the card the change came from, carried onto the event so a reader can name it.
 */
export function gainLore(
  state: GameState,
  player: PlayerIndex,
  amount: number,
  source: InstanceId | null = null,
): void {
  if (amount === 0) return;
  const self = playerAt(state, player);
  const delta = Math.max(amount, -self.lore);
  if (delta === 0) return;
  self.lore += delta;
  emit(state, { type: 'lore-changed', player, delta, total: self.lore, source });
}

/** Every instance in a player's zone, as records. */
export function cardsIn(state: GameState, player: PlayerIndex, zone: Zone): CardInstance[] {
  return zoneOf(playerAt(state, player), zone).map((id) => instanceAt(state, id));
}

/** The first card in hand with this printed id — how a `d`-keyed option resolves. */
export function findInHandByDef(
  state: GameState,
  player: PlayerIndex,
  defId: number,
  exclude: readonly InstanceId[] = [],
): CardInstance | undefined {
  for (const id of playerAt(state, player).hand) {
    if (exclude.includes(id)) continue;
    const instance = instanceAt(state, id);
    if (instance.defId === defId) return instance;
  }
  return undefined;
}

/** Ready ink cards available to pay a cost. */
export function availableInk(state: GameState, player: PlayerIndex): number {
  let ready = 0;
  for (const id of playerAt(state, player).inkwell) {
    if (!instanceAt(state, id).exerted) ready += 1;
  }
  return ready;
}

/**
 * Pay `amount` ink by exerting ready ink cards in inkwell order (CR §4.3.3). Which cards
 * are exerted is not a player choice — every ink card is worth exactly 1 ink (CR §7.5.5).
 */
export function payInk(state: GameState, player: PlayerIndex, amount: number): void {
  if (amount <= 0) return;
  const exerted: InstanceId[] = [];
  for (const id of playerAt(state, player).inkwell) {
    if (exerted.length === amount) break;
    const instance = instanceAt(state, id);
    if (instance.exerted) continue;
    instance.exerted = true;
    exerted.push(id);
  }
  if (exerted.length < amount) {
    throw new Error(`Player ${player} cannot pay ${amount} ink`);
  }
  emit(state, { type: 'ink-exerted', player, instances: exerted, amount });
}

/**
 * Exert a character — the shared chokepoint every direct `exerted = true` mutation on a card
 * funnels through, so "whenever a character becomes exerted" (Grimorum Arcanorum) sees every
 * reason: questing, challenging, an activated ability's `{E}` cost, and any effect that exerts
 * one.
 *
 * Idempotent: exerting an already-exerted card is a no-op and emits nothing (the mirror of
 * `ready`'s `if (!card.exerted) continue` guard). And it only ever announces a **character**
 * already **in play** — a card sitting in the inkwell has stopped functioning as its printed
 * type (`payInk` exerts those and emits `ink-exerted` instead, never through here), and a
 * Location does not "become" exerted the way a character does.
 *
 * Not what "enters play exerted" uses (`rules.ts`'s Bodyguard choice and `enter-play-ready`
 * restriction, `moveCardTo`'s `exerted` option) — a character landing in play already exerted
 * never was ready, so it has nothing to become.
 */
export function exertCharacter(state: GameState, id: InstanceId): void {
  const instance = instanceAt(state, id);
  if (instance.exerted) return;
  instance.exerted = true;
  if (instance.zone !== 'play' || definitionOf(state, instance).type !== 'Character') return;
  emit(state, {
    type: 'character-exerted',
    player: instance.controller,
    instance: id,
    defId: instance.defId,
  });
}

/** Characters this player controls in play. */
export function charactersInPlay(state: GameState, player: PlayerIndex): CardInstance[] {
  return cardsIn(state, player, 'play').filter(
    (instance) => definitionOf(state, instance).type === 'Character',
  );
}
